import React, { useEffect } from 'react';

import { ProButton } from '@components/Button/variants';
import IconDeepResearchVertical from '@components/Images/DeepResearch/IconDeepResearchVertical';
import IconClose from '@components/Images/Rebranding/IconClose';
import TIModal, { TiModalColorScheme } from '@components/TIModal';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import { discounted_upgrade_path } from '@routes';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';

const LEARN_MORE_TEXT = 'Learn more';
const MONTHLY_USER_UPGRADE_CODE = 'modal-deepai-pro-mon-699-july-25';
const ANNUAL_USER_UPGRADE_CODE = 'modal-deepai-pro-ann-699-july-25';
const CBPT_USER_UPGRADE_CODE = 'modal-deepai-pro-cbpt-699-july-25';

interface SearchUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchUpgradeModal = ({ isOpen, onClose }: SearchUpgradeModalProps) => {
  const { currentUser } = useCurrentUser();
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.Modal,
  });

  const upgradeOffer = currentUser?.subscription?.is_monthly
    ? MONTHLY_USER_UPGRADE_CODE
    : currentUser?.subscription?.is_cbpt
    ? CBPT_USER_UPGRADE_CODE
    : ANNUAL_USER_UPGRADE_CODE;

  useEffect(() => {
    if (isOpen) {
      trackEvent(impression({ impressionElement: ImpressionElement.DeepResearchToggleModal }));
    }
  }, [isOpen, trackEvent]);

  return (
    <TIModal
      id="deep-research-modal"
      isOpen={isOpen}
      onClose={onClose}
      customHeader={true}
      isFullScreen={false}
      colorScheme={TiModalColorScheme.light}
      parentClass="w-full max-w-full xs:max-w-sm sm:max-w-md"
      disableScrollLock={false}
    >
      <div className="absolute right-8 top-8 flex justify-end sm:right-16 sm:top-16">
        <IconClose
          role="button"
          aria-label="Close modal"
          className="h-[26px] w-[26px] cursor-pointer text-black"
          onClick={onClose}
          data-test="close-modal"
          strokeWidth="1"
        />
      </div>
      <div className="space-y-24 p-32">
        <div className="text-center">
          <IconDeepResearchVertical className="mt-24 w-[119px]" />
          <p className="mb-0 font-suisse-intl text-body-lg text-primary-black">
            Deep Research is a benefit exclusively for The Information Pro subscribers. Save over
            $200 when you upgrade today.
          </p>
        </div>
        <div className="flex justify-center">
          <ProButton
            as="link"
            href={discounted_upgrade_path({ offer: upgradeOffer })}
            onClickCapture={() =>
              trackEvent(
                click({
                  clickTarget: ClickTarget.DeepResearchUpgradeCTA,
                  clickText: LEARN_MORE_TEXT,
                  clickLink: discounted_upgrade_path({ offer: upgradeOffer }),
                })
              )
            }
            className="w-full px-32 py-12"
          >
            {LEARN_MORE_TEXT}
          </ProButton>
        </div>
      </div>
    </TIModal>
  );
};

export default SearchUpgradeModal;
